import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../hooks/useTier';
import { COPY_FEEDBACK_DURATION, STALE_PEER_THRESHOLD, FRESH_PEER_THRESHOLD, PEER_CONNECTION_DELAY, FALLBACK_CONNECTION_DELAY, MAX_RETRY_DELAY, RATE_LIMIT_COOLDOWN } from '../constants/timing';
import { db } from '../config/firebase';
import { collection, doc, setDoc, onSnapshot, deleteDoc, serverTimestamp, query, where, getDocs, getDoc, addDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import FirebaseWebRTCSignaling from '../utils/firebaseWebRTCSignaling';
import AdPlayer from '../components/AdPlayer';
import ChatBox from '../components/ChatBox';
import MeetingNotes from '../components/MeetingNotes';
import MeetingTimer from '../components/MeetingTimer';
import MeetingCostCalculator from '../components/MeetingCostCalculator';
import RaiseHandQueue from '../components/RaiseHandQueue';
import TalkTimeAnalytics from '../components/TalkTimeAnalytics';
import ShareMeetingLink from '../components/ShareMeetingLink';
import ShareMeetingModal from '../components/ShareMeetingModal';
import VirtualBackgrounds from '../components/VirtualBackgrounds';
import { ThemeSelector } from '../components/MeetingThemes';
import MeetingPrompts from '../components/MeetingPrompts';
import WaitingRoomQueue from '../components/WaitingRoomQueue';
import VirtualBackgroundProcessor from '../utils/virtualBackgroundProcessor';
import { useRecording } from '../hooks/useRecording';
import { useAudioClip } from '../hooks/useAudioClip';
import ClipRecordingIndicator from '../components/ClipRecordingIndicator';
import { playSound, SOUNDS } from '../utils/soundEffects';
import { startHeartbeat, stopHeartbeat } from '../services/meetingHeartbeat';

export default function VideoCall() {
  const { meetingId } = useParams();
  const { user, userProfile } = useAuth();
  const { isPremium } = useTier();
  const navigate = useNavigate();

  // Check if user already watched ad for this meeting in this session
  const hasWatchedAd = sessionStorage.getItem(`quickmeet_ads_watched_${meetingId}`) === 'true';

  const [showAd, setShowAd] = useState(!hasWatchedAd);
  const [adCompleted, setAdCompleted] = useState(hasWatchedAd);

  const [myPeerId, setMyPeerId] = useState('');
  const [remotePeerIds, setRemotePeerIds] = useState([]);
  const [participantData, setParticipantData] = useState({}); // Store participant names
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isPiPMode, setIsPiPMode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [connections, setConnections] = useState({});
  const [connectionStats, setConnectionStats] = useState({});
  const [networkQuality, setNetworkQuality] = useState({ latency: null, quality: 'good' });
  const connectingPeersRef = useRef(new Set());

  // Chat state
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0); // Track unread messages
  const [dataConnections, setDataConnections] = useState({});
  const dataConnectionsRef = useRef({});

  // Meeting notes state
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [notes, setNotes] = useState([]);

  // Auto-hide controls state - visible by default
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimerRef = useRef(null);
  const [showParticipants, setShowParticipants] = useState(false);

  // Ensure only one drawer is open at a time
  useEffect(() => {
    if (showParticipants) {
      setIsChatOpen(false);
      setIsNotesOpen(false);
    }
  }, [showParticipants]);

  useEffect(() => {
    if (isChatOpen) {
      setShowParticipants(false);
      setIsNotesOpen(false);
    }
  }, [isChatOpen]);

  useEffect(() => {
    if (isNotesOpen) {
      setShowParticipants(false);
      setIsChatOpen(false);
    }
  }, [isNotesOpen]);

  // New features state
  const [meetingStartTime, setMeetingStartTime] = useState(null);
  const [participantsList, setParticipantsList] = useState([]);
  const [raisedHands, setRaisedHands] = useState([]);
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [showCostCalculator, setShowCostCalculator] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [talkTime, setTalkTime] = useState({});
  const talkTimeIntervalRef = useRef(null);
  const [meetingData, setMeetingData] = useState(null);
  const [localStreamReady, setLocalStreamReady] = useState(0); // Trigger re-render when stream changes
  const [videoElementMounted, setVideoElementMounted] = useState(false); // Track when video element is ready

  // Waiting room state
  const [inWaitingRoom, setInWaitingRoom] = useState(false);
  const [admissionStatus, setAdmissionStatus] = useState(null); // 'admitted', 'rejected', or null

  // Meeting status state for iOS-compatible status handling
  const [meetingStatus, setMeetingStatus] = useState(null); // 'active' | 'ended' | 'cancelled' | 'scheduled' | 'waiting' | 'not_found' | null

  // Video layout state
  const [featuredParticipant, setFeaturedParticipant] = useState(null); // For mobile featured view
  const [showLinkBubble, setShowLinkBubble] = useState(true); // Copy link bubble visibility
  const [virtualBackground, setVirtualBackground] = useState({ id: 'none', type: 'none' }); // Virtual background state
  const [showShareModal, setShowShareModal] = useState(false); // Share meeting modal

  // Popup states - only one can be open at a time
  const [activePopup, setActivePopup] = useState(null); // 'virtualBackground', or null

  // Helper function to toggle popups with mutual exclusivity
  const togglePopup = (popupName) => (newState) => {
    if (typeof newState === 'boolean') {
      setActivePopup(newState ? popupName : null);
    } else {
      setActivePopup(activePopup === popupName ? null : popupName);
    }
  };

  // Check if user is premium (has pro or team tier)

  // Check if user is platform admin
  const isAdmin = user?.email === 'omeleyjhye@gmail.com';

  // Check if user is meeting organizer
  const isOrganizer = meetingData?.createdBy === user?.uid;

  const localVideoRef = useRef(null);

  // Callback ref to know when video element mounts - memoized to prevent infinite loops
  const setLocalVideoRef = useCallback((element) => {
    if (localVideoRef.current !== element) {
      localVideoRef.current = element;
      if (element) {
        console.log('📹 Video element mounted!');
        setVideoElementMounted(true);
      } else {
        setVideoElementMounted(false);
      }
    }
  }, []);
  const remoteVideosRef = useRef({});
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const signalingServiceRef = useRef(null);
  const activeParticipantsRef = useRef([]);
  const backgroundProcessorRef = useRef(null);
  const processedCanvasRef = useRef(null);

  // Recording hook and refs
  const {
    isRecording,
    recordingTime,
    uploadProgress,
    isUploading,
    startRecording,
    stopRecording,
    uploadRecording
  } = useRecording(user?.uid, userProfile);

  // Audio clip for personal context notes (local mic only)
  const {
    isBuffering: isClipBuffering,
    isRecording: isClipRecording,
    recordingTime: clipRecordingTime,
    hasPermission: hasClipPermission,
    isUploading: isClipUploading,
    startBuffer: startClipBuffer,
    stopBuffer: stopClipBuffer,
    startClipRecording,
    stopClipRecording,
    uploadClip,
    maxDuration: clipMaxDuration
  } = useAudioClip(user?.uid);

  // Track if meeting mic is muted (for clip recording UI)
  const [isMeetingMicMuted, setIsMeetingMicMuted] = useState(false);

  // Pending clip to attach to next note
  const [pendingClipForNote, setPendingClipForNote] = useState(null);

  const recordingCanvasRef = useRef(null);
  const recordingAnimationRef = useRef(null);

  // Ensure local video displays when stream is ready
  useEffect(() => {
    const debugInfo = {
      hasStream: !!localStreamRef.current,
      hasVideoElement: !!localVideoRef.current,
      videoElementMounted,
      isScreenSharing,
      localStreamReady,
      streamActive: localStreamRef.current?.active,
      videoElement: localVideoRef.current ? 'exists' : 'null'
    };
    console.log('Video display effect triggered:', debugInfo);

    if (localStreamRef.current && localVideoRef.current && !isScreenSharing) {
      console.log('✅ All conditions met, setting video srcObject');
      console.log('Stream tracks:', localStreamRef.current.getTracks().map(t => ({
        kind: t.kind,
        enabled: t.enabled,
        readyState: t.readyState
      })));

      localVideoRef.current.srcObject = localStreamRef.current;

      // Ensure local video is muted to prevent hearing yourself
      localVideoRef.current.muted = true;
      localVideoRef.current.volume = 0;

      // Force play
      localVideoRef.current.play().then(() => {
        console.log('✅ Video play successful');
      }).catch(err => {
        console.error('❌ Video play failed:', err);
      });
    } else {
      console.log('❌ Conditions not met:', {
        hasStream: !!localStreamRef.current,
        hasVideoElement: !!localVideoRef.current,
        videoElementMounted,
        isScreenSharing: isScreenSharing
      });
    }
  }, [localStreamReady, isScreenSharing, videoElementMounted]);

  // Auto-skip ad for platform admin
  useEffect(() => {
    if (isAdmin && !adCompleted) {
      setAdCompleted(true);
      setShowAd(false);
      sessionStorage.setItem(`quickmeet_ads_watched_${meetingId}`, 'true');
    }
  }, [isAdmin, adCompleted, meetingId]);

  // Start local stream once ad is completed
  useEffect(() => {
    if (adCompleted && !localStreamRef.current) {
      console.log('Ad completed, starting local stream');
      startLocalStream();
    }
  }, [adCompleted]);

  // Track meeting start time when participant ID is set
  useEffect(() => {
    if (myPeerId && !meetingStartTime) {
      setMeetingStartTime(Date.now());
    }
  }, [myPeerId, meetingStartTime]);

  // Track all participants (including those who left)
  useEffect(() => {
    setParticipantsList(prev => {
      const allParticipants = [
        user?.email || 'You',
        ...remotePeerIds.map(id => participantData[id]?.userName || 'Guest')
      ];
      // Merge with previous, keeping unique entries
      return [...new Set([...prev, ...allParticipants])];
    });
  }, [remotePeerIds, participantData, user]);

  // Monitor connection quality
  useEffect(() => {
    if (Object.keys(connections).length === 0) return;

    const checkConnectionQuality = async () => {
      try {
        const connectionArray = Object.values(connections);
        if (connectionArray.length === 0) return;

        const call = connectionArray[0];
        if (!call?.peerConnection) return;

        const stats = await call.peerConnection.getStats();
        let latency = null;

        stats.forEach(report => {
          if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            latency = report.currentRoundTripTime ? Math.round(report.currentRoundTripTime * 1000) : null;
          }
        });

        if (latency !== null) {
          let quality = 'good';
          if (latency > 200) quality = 'poor';
          else if (latency > 100) quality = 'medium';

          setNetworkQuality({ latency, quality });
        }
      } catch (err) {
        console.error('Error checking connection quality:', err);
      }
    };

    const interval = setInterval(checkConnectionQuality, RATE_LIMIT_COOLDOWN);
    checkConnectionQuality(); // Run immediately

    return () => clearInterval(interval);
  }, [connections]);

  // Clear unread messages when chat is opened
  useEffect(() => {
    if (isChatOpen) {
      setUnreadCount(0);
    }
  }, [isChatOpen]);

  // Note: Removed auto-close chat when alone - users may want to prepare messages

  // Auto-hide controls system
  useEffect(() => {
    const showControls = () => {
      setControlsVisible(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      // Extended hide timer - 5 seconds for better usability
      hideTimerRef.current = setTimeout(() => setControlsVisible(false), 5000);
    };

    const handleActivity = () => showControls();

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('touchstart', handleActivity);

    // Show controls initially
    showControls();

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  // Close drawers with ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (isChatOpen) setIsChatOpen(false);
        else if (isNotesOpen) setIsNotesOpen(false);
        else if (showParticipants) setShowParticipants(false);
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isChatOpen, isNotesOpen, showParticipants]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Don't trigger shortcuts if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'm':
          toggleMute();
          break;
        case 'v':
          toggleVideo();
          break;
        case 's':
          toggleScreenShare();
          break;
        case 'c':
          setIsChatOpen(prev => !prev);
          break;
        case 'p':
          togglePiP();
          break;
        case 'f':
          if (document.fullscreenElement) {
            document.exitFullscreen();
          } else {
            document.documentElement.requestFullscreen();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isMuted, isVideoOff, isScreenSharing, isChatOpen]);

  useEffect(() => {
    const participantId = `${meetingId}-${Date.now()}`;
    let signalingService = null;
    let unsubscribe = null;

    const initializeSignaling = async () => {
      console.log('🔥 Initializing Firebase WebRTC signaling');

      setMyPeerId(participantId);

      // Create signaling service
      signalingService = new FirebaseWebRTCSignaling(
        meetingId,
        participantId,
        // onRemoteStream callback
        (remoteParticipantId, remoteStream) => {
          console.log('📺 Received remote stream from:', remoteParticipantId);
          addRemoteStream(remoteParticipantId, remoteStream);
        },
        // onConnectionStateChange callback
        (remoteParticipantId, state) => {
          console.log('🔌 Connection state with', remoteParticipantId, ':', state);
          if (state === 'disconnected' || state === 'failed' || state === 'closed') {
            removeRemoteStream(remoteParticipantId);
          }
        }
      );

      // Store in ref for access from other effects
      signalingServiceRef.current = signalingService;

      // Start listening for signaling messages
      signalingService.startListening();

      // Set local stream if already available
      if (localStreamRef.current) {
        signalingService.setLocalStream(localStreamRef.current);
      }

      // Add participant to Firebase
      const meetingDocRef = doc(db, 'meetings', meetingId);

      // Participant data with both Web and iOS-compatible fields
      const participantData = {
        id: participantId,
        odcId: user?.uid || participantId,  // iOS uses odcId
        userId: user?.uid || 'anonymous',
        displayName: userProfile?.displayName || user?.email || 'Guest',
        odcName: userProfile?.displayName || user?.email || 'Guest',  // iOS uses odcName
        profilePictureURL: userProfile?.profilePicture || null,
        joinedAt: Math.floor(Date.now() / 1000),  // iOS uses seconds, not milliseconds
        isActive: true
      };

      console.log('🌐 Web user joining meeting:', meetingId);
      console.log('🌐 Adding participant:', participantData);

      // Write to both 'participants' and 'participantsData' arrays for cross-platform compatibility
      await updateDoc(meetingDocRef, {
        participants: arrayUnion(participantData),
        participantsData: arrayUnion(participantData)
      });

      // Also write to participants/ subcollection for iOS
      const participantDocRef = doc(db, 'meetings', meetingId, 'participants', participantId);
      await setDoc(participantDocRef, {
        odcId: user?.uid || participantId,
        odcName: userProfile?.displayName || user?.email || 'Guest',
        odcProfilePicture: userProfile?.profilePicture || null,
        joinedAt: Math.floor(Date.now() / 1000),
        isActive: true
      });

      console.log('✅ Participant added to Firebase');

      // Verify participants
      const meetingSnap = await getDoc(meetingDocRef);
      if (meetingSnap.exists()) {
        const participants = meetingSnap.data().participants || [];
        console.log('📋 All participants in Firebase:', participants.length);
        participants.forEach((p, i) => {
          console.log(`Participant ${i + 1}:`, {
            id: p.id,
            displayName: p.displayName,
            isActive: p.isActive
          });
        });
      }

      // Wait for Firebase to sync
      await new Promise(resolve => setTimeout(resolve, 1000));

      console.log('✅ Firebase sync complete, starting WebRTC signaling');

      // Listen for active participants
      unsubscribe = onSnapshot(meetingDocRef, (snapshot) => {
        if (!snapshot.exists()) return;

        const meetingData = snapshot.data();
        // iOS uses 'participantsData' array, Web may use 'participants' - prefer participantsData
        const participants = meetingData.participantsData || meetingData.participants || [];

        // Normalize participant data - handle both iOS (odcId/odcName) and Web (id/userId/displayName) formats
        const normalizedParticipants = participants.map(p => ({
          id: p.id || p.odcId || p.odId,  // iOS might use odcId or odId
          odcId: p.odcId || p.odId || p.userId,
          displayName: p.displayName || p.odcName || p.odName || 'Participant',
          userId: p.userId || p.odcId || p.odId,
          profilePictureURL: p.profilePictureURL || p.photoURL || null,
          isActive: p.isActive !== undefined ? p.isActive : true  // Default to active if not specified
        }));

        const activeParticipants = normalizedParticipants.filter(p => p.isActive && p.id !== participantId);

        console.log('👥 Active participants:', activeParticipants.length);

        // Store participant data for UI
        const newParticipantData = {};
        activeParticipants.forEach(p => {
          newParticipantData[p.id] = {
            userName: p.displayName,
            userId: p.userId,
            profilePictureURL: p.profilePictureURL
          };
        });
        setParticipantData(newParticipantData);

        // Store active participants in ref (use normalized data)
        activeParticipantsRef.current = activeParticipants;

        // Only create offers if local stream is already set
        if (signalingService.localStream) {
          activeParticipants.forEach(p => {
            // Only higher ID initiates to avoid duplicate connections
            if (participantId > p.id) {
              console.log('🔗 Creating offer for:', p.id);
              signalingService.createOffer(p.id);
            }
          });
        } else {
          console.log('⏳ Local stream not ready yet, offers will be created when stream is set');
        }
      });
    };

    // Wait for ad to complete before initializing
    if (adCompleted) {
      initializeSignaling();
    }

    return () => {
      console.log('🧹 Cleaning up Firebase signaling');
      if (signalingService) {
        signalingService.cleanup();
      }
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [meetingId, adCompleted, user, userProfile]);

  // Start heartbeat when participant ID is set
  useEffect(() => {
    if (meetingId && myPeerId) {
      console.log('💓 Starting heartbeat for meeting participant');
      const stopFn = startHeartbeat(meetingId, myPeerId);

      // Cleanup on unmount or when meeting/peer changes
      return () => {
        console.log('💔 Stopping heartbeat');
        stopFn();
      };
    }
  }, [meetingId, myPeerId]);

  // Start audio clip buffer for Pro users when meeting is active
  // Use refs to avoid dependency issues that cause infinite re-renders
  const clipBufferStartedRef = useRef(false);
  const clipBufferFnsRef = useRef({ start: startClipBuffer, stop: stopClipBuffer });

  // Keep function refs updated without triggering effect
  useEffect(() => {
    clipBufferFnsRef.current = { start: startClipBuffer, stop: stopClipBuffer };
  });

  useEffect(() => {
    if (meetingId && myPeerId && isPremium && !clipBufferStartedRef.current) {
      clipBufferStartedRef.current = true;
      clipBufferFnsRef.current.start();
    }

    // Cleanup buffer only when leaving meeting (unmount)
    return () => {
      if (clipBufferStartedRef.current) {
        clipBufferStartedRef.current = false;
        clipBufferFnsRef.current.stop();
      }
    };
  }, [meetingId, myPeerId, isPremium]);

  // Set local stream on signaling service when available
  useEffect(() => {
    if (localStreamRef.current && signalingServiceRef.current) {
      console.log('🎥 Local stream ready, updating signaling service');
      signalingServiceRef.current.setLocalStream(localStreamRef.current);

      // Now that stream is ready, create offers for any active participants
      if (activeParticipantsRef.current.length > 0 && myPeerId) {
        console.log('🔗 Local stream now available, creating offers for', activeParticipantsRef.current.length, 'participants');
        activeParticipantsRef.current.forEach(p => {
          // Only higher ID initiates to avoid duplicate connections
          if (myPeerId > p.id) {
            console.log('🔗 Creating offer for:', p.id);
            signalingServiceRef.current.createOffer(p.id);
          }
        });
      }
    }
  }, [localStreamRef.current, myPeerId]);

  const startLocalStream = async () => {
    try {
      // Video quality based on subscription
      // Using isPremium from useTier hook
      const videoConstraints = isPremium ? {
        width: { ideal: 3840 },
        height: { ideal: 2160 },
        frameRate: { ideal: 60 }
      } : {
        width: { ideal: 1280, max: 1280 },
        height: { ideal: 720, max: 720 },
        frameRate: { ideal: 30, max: 30 }
      };

      console.log(`Starting stream with ${isPremium ? '4K (Premium)' : '720p (Free)'} quality`);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      localStreamRef.current = stream;

      // Ensure video and audio are enabled
      stream.getVideoTracks().forEach(track => track.enabled = true);
      stream.getAudioTracks().forEach(track => track.enabled = !isMuted);

      console.log('Local stream acquired, triggering video display');

      // Trigger useEffect to update video element
      setLocalStreamReady(prev => prev + 1);

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        // CRITICAL: Mute local video to prevent hearing yourself
        localVideoRef.current.muted = true;
        localVideoRef.current.volume = 0;
        // Explicitly play the video to ensure it displays
        try {
          await localVideoRef.current.play();
        } catch (playErr) {
          console.log('Autoplay prevented, video will play on user interaction');
        }
      }
    } catch (err) {
      console.error('Error accessing media devices:', err);
      alert('Please allow camera and microphone access to join the meeting');
    }
  };

  const addRemoteStream = (peerId, stream) => {
    console.log('🎬 addRemoteStream called for peer:', peerId, {
      streamActive: stream?.active,
      videoTracks: stream?.getVideoTracks().length,
      audioTracks: stream?.getAudioTracks().length
    });

    // Check if already added to prevent duplicates
    if (remoteVideosRef.current[peerId]?.srcObject) {
      console.log('Stream already exists for peer:', peerId);
      return;
    }

    setRemotePeerIds(prev => {
      const newIds = !prev.includes(peerId) ? [...prev, peerId] : prev;
      console.log('Updated remotePeerIds:', newIds);
      return newIds;
    });

    // Wait for video element to be rendered
    setTimeout(() => {
      const videoElement = remoteVideosRef.current[peerId];
      console.log('Attempting to attach stream to video element:', {
        peerId,
        hasVideoElement: !!videoElement,
        hasStream: !!stream
      });

      if (videoElement && stream) {
        videoElement.srcObject = stream;
        videoElement.play().then(() => {
          console.log('✅ Remote video playing for peer:', peerId);
        }).catch(err => {
          console.error('❌ Failed to play remote video for peer:', peerId, err);
        });
        console.log('Remote stream attached to video element for peer:', peerId);
      } else {
        console.error('❌ Cannot attach stream - missing element or stream:', {
          peerId,
          hasElement: !!videoElement,
          hasStream: !!stream
        });
      }
    }, 200);
  };

  const removeRemoteStream = (peerId) => {
    setRemotePeerIds(prev => prev.filter(id => id !== peerId));
    setParticipantData(prev => {
      const updated = { ...prev };
      delete updated[peerId];
      return updated;
    });
    delete remoteVideosRef.current[peerId];

    // Play leaving sound when participant disconnects
    playSound(SOUNDS.LEAVING_MEETING);
  };

  const connectToPeer = (remotePeerId, retryCount = 0) => {
    const currentPeer = peerRef.current; // Use ref instead of state

    if (!currentPeer || !localStreamRef.current) {
      console.log('Not ready to connect, retrying...', {
        peer: !!currentPeer,
        stream: !!localStreamRef.current,
        retryCount
      });
      // Retry with exponential backoff (up to 10 attempts over ~25 seconds)
      if (retryCount < 10) {
        const delay = Math.min(500 * Math.pow(1.5, retryCount), MAX_RETRY_DELAY);
        setTimeout(() => connectToPeer(remotePeerId, retryCount + 1), delay);
      } else {
        console.error('Failed to connect after 10 retries - peer or stream not ready');
        connectingPeersRef.current.delete(remotePeerId);
      }
      return;
    }

    // Don't connect if already connected or currently connecting
    if (connections[remotePeerId]) {
      console.log('Already connected to', remotePeerId);
      return;
    }

    if (connectingPeersRef.current.has(remotePeerId)) {
      console.log('Already connecting to', remotePeerId);
      return;
    }

    connectingPeersRef.current.add(remotePeerId);
    console.log('Calling peer:', remotePeerId);

    try {
      const call = currentPeer.call(remotePeerId, localStreamRef.current);

      if (!call) {
        console.error('Failed to create call to', remotePeerId);
        connectingPeersRef.current.delete(remotePeerId);
        return;
      }

      call.on('stream', (remoteStream) => {
        console.log('Received stream from outgoing call:', remotePeerId);
        addRemoteStream(remotePeerId, remoteStream);
        connectingPeersRef.current.delete(remotePeerId);

        // Get connection stats to verify P2P
        if (call.peerConnection) {
          call.peerConnection.getStats().then(stats => {
            stats.forEach(report => {
              if (report.type === 'candidate-pair' && report.state === 'succeeded') {
                setConnectionStats(prev => ({
                  ...prev,
                  [remotePeerId]: {
                    type: report.candidateType || 'p2p',
                    state: report.state,
                    encrypted: true
                  }
                }));
              }
            });
          });
        }
      });

      call.on('close', () => {
        console.log('Outgoing call closed:', remotePeerId);
        removeRemoteStream(remotePeerId);
        connectingPeersRef.current.delete(remotePeerId);
      });

      call.on('error', (err) => {
        console.error('Outgoing call error:', remotePeerId, err);
        connectingPeersRef.current.delete(remotePeerId);

        // Remove failed connection
        setConnections(prev => {
          const updated = { ...prev };
          delete updated[remotePeerId];
          return updated;
        });

        // Retry after a delay (will use exponential backoff)
        setTimeout(() => {
          console.log('Retrying connection to', remotePeerId, 'after error');
          connectToPeer(remotePeerId, 0);
        }, 3000);
      });

      setConnections(prev => ({ ...prev, [remotePeerId]: call }));
    } catch (err) {
      console.error('Exception calling peer:', err);
      connectingPeersRef.current.delete(remotePeerId);
    }
  };

  // Chat functions
  const createDataConnection = (remotePeerId) => {
    const currentPeer = peerRef.current;

    if (!currentPeer) {
      console.log('Peer not ready for data connection');
      return;
    }

    // Don't create duplicate data connections
    if (dataConnectionsRef.current[remotePeerId]) {
      console.log('Data connection already exists with', remotePeerId);
      return;
    }

    console.log('Creating data connection with', remotePeerId);
    const conn = currentPeer.connect(remotePeerId, { reliable: true });

    conn.on('open', () => {
      console.log('Data connection opened with', remotePeerId);
      dataConnectionsRef.current[remotePeerId] = conn;
      setDataConnections(prev => ({ ...prev, [remotePeerId]: conn }));
    });

    conn.on('data', async (data) => {
      console.log('Received message from', remotePeerId, ':', data);
      if (data.type === 'chat') {
        const message = {
          sender: data.sender,
          message: data.message,
          timestamp: data.timestamp
        };
        setMessages(prev => [...prev, message]);

        // Save to Firestore if enabled
        if (meetingData?.saveChatHistory) {
          try {
            const chatRef = collection(db, 'meetings', meetingId, 'chatHistory');
            await addDoc(chatRef, {
              ...message,
              timestamp: serverTimestamp()
            });
          } catch (err) {
            console.error('Error saving chat:', err);
          }
        }

        // Increment unread count if chat is closed
        if (!isChatOpen) {
          setUnreadCount(prev => prev + 1);
        }

        // Play new message sound
        playSound(SOUNDS.NEW_MESSAGE);
      } else if (data.type === 'mute_others') {
        // Handle mute command from organizer
        const currentUserId = user?.uid || 'anonymous';
        if (currentUserId !== data.speakerUserId) {
          // Mute self (not the selected speaker)
          if (localStreamRef.current) {
            const audioTrack = localStreamRef.current.getAudioTracks()[0];
            if (audioTrack) {
              audioTrack.enabled = false;
              setIsMuted(true);
            }
          }
          console.log('Muted by organizer - speaker is:', data.speakerUserId);
        } else {
          // Unmute if you're the selected speaker
          if (localStreamRef.current) {
            const audioTrack = localStreamRef.current.getAudioTracks()[0];
            if (audioTrack) {
              audioTrack.enabled = true;
              setIsMuted(false);
            }
          }
          console.log('Unmuted by organizer - you are the speaker');
        }
      }
    });

    conn.on('close', () => {
      console.log('Data connection closed with', remotePeerId);
      delete dataConnectionsRef.current[remotePeerId];
      setDataConnections(prev => {
        const newConns = { ...prev };
        delete newConns[remotePeerId];
        return newConns;
      });
    });

    conn.on('error', (err) => {
      console.error('Data connection error with', remotePeerId, err);
    });
  };

  const handleSendMessage = async (messageText) => {
    const newMessage = {
      sender: user?.email || 'Guest',
      senderId: user?.uid,
      message: messageText,
      timestamp: Date.now()
    };

    const messageData = {
      type: 'chat',
      ...newMessage
    };

    // Send via P2P (existing logic)
    Object.values(dataConnectionsRef.current).forEach(conn => {
      if (conn.open) {
        conn.send(messageData);
      }
    });

    // Add to local state
    setMessages(prev => [...prev, messageData]);

    // Save to Firestore if enabled
    if (meetingData?.saveChatHistory) {
      try {
        const chatRef = collection(db, 'meetings', meetingId, 'chatHistory');
        await addDoc(chatRef, {
          ...newMessage,
          timestamp: serverTimestamp()
        });
      } catch (err) {
        console.error('Error saving chat:', err);
      }
    }
  };

  // Meeting notes function
  const handleAddNote = async (content, isPrivate, meetingDuration) => {
    // Using isPremium from useTier hook
    if (!isPremium) {
      console.warn('Notes feature requires Pro subscription');
      return;
    }

    // Capture pending clip from state (will be cleared after note is added)
    const clipToAttach = pendingClipForNote;

    try {
      const notesRef = collection(db, 'meetings', meetingId, 'notes');

      // Create note document first (notes are primary, audio is secondary)
      const noteData = {
        content,
        isPrivate,
        meetingDuration, // Minutes into meeting
        userName: user?.email || 'Guest',
        userId: user?.uid,
        timestamp: Date.now(),
        createdAt: serverTimestamp(),
        // Initialize audioClip as null, will be updated after upload
        audioClip: null
      };

      const noteDoc = await addDoc(notesRef, noteData);

      // Clear pending clip immediately after note is created
      if (clipToAttach) {
        setPendingClipForNote(null);
      }

      // If there's a clip to attach, upload it asynchronously (silent failure)
      if (clipToAttach?.blob && user?.uid) {
        // Upload in background - don't await, don't block
        uploadClip(clipToAttach.blob, meetingId, noteDoc.id, clipToAttach.duration)
          .then(async (clipData) => {
            if (clipData?.url) {
              // Update the note with clip data
              try {
                await updateDoc(doc(db, 'meetings', meetingId, 'notes', noteDoc.id), {
                  audioClip: {
                    url: clipData.url,
                    duration: clipData.duration,
                    size: clipData.size,
                    storagePath: clipData.storagePath,
                    recordedAt: serverTimestamp()
                  }
                });
                console.log('[AudioClip] Clip attached to note:', noteDoc.id);
              } catch {
                // Silent failure - clip upload succeeded but note update failed
                console.warn('[AudioClip] Failed to update note with clip data');
              }
            }
          })
          .catch(() => {
            // Silent failure - audio upload failed, but note is already saved
            console.warn('[AudioClip] Clip upload failed silently');
          });
      }
    } catch (err) {
      console.error('Error adding note:', err);
    }
  };

  // Handle clip recording start
  const handleStartClipRecording = useCallback(() => {
    // Mute meeting mic when starting clip recording
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = false;
        setIsMeetingMicMuted(true);
        setIsMuted(true); // Update UI state
      }
    }
    startClipRecording(() => {
      // This callback is for muting - already handled above
    });
  }, [startClipRecording]);

  // Handle clip recording stop
  const handleStopClipRecording = useCallback(async () => {
    const clipData = await stopClipRecording();

    // Note: Don't unmute automatically - let user control that
    if (clipData) {
      // Set pending clip to be attached to next note
      setPendingClipForNote(clipData);

      // Open notes drawer if not open (so user can add the note)
      if (!isNotesOpen) {
        setIsNotesOpen(true);
      }
    }
  }, [stopClipRecording, isNotesOpen]);

  // Load meeting notes in real-time
  useEffect(() => {
    if (!meetingId || !user?.uid) return;

    const notesRef = collection(db, 'meetings', meetingId, 'notes');

    const unsubscribe = onSnapshot(notesRef, (snapshot) => {
      const loadedNotes = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(note => {
          // Show shared notes OR private notes that belong to current user
          return !note.isPrivate || note.userId === user?.uid;
        })
        .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)); // Sort by timestamp

      setNotes(loadedNotes);
    }, (error) => {
      // Gracefully handle permission errors - notes will be empty but app continues working
      if (error.code === 'permission-denied') {
        console.warn('Unable to load meeting notes - check Firestore rules deployment');
        setNotes([]);
      } else {
        console.error('Error loading notes:', error);
      }
    });

    return () => unsubscribe();
  }, [meetingId, user]);

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        const newMutedState = !audioTrack.enabled;
        setIsMuted(newMutedState);

        // Play sound when muting
        if (newMutedState) {
          playSound(SOUNDS.MIC_MUTED);
        }
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      // Stop screen sharing
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }
      await startLocalStream();
      setIsScreenSharing(false);
    } else {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: true
        });
        screenStreamRef.current = screenStream;

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = screenStream;
          // Mute local video to prevent hearing yourself
          localVideoRef.current.muted = true;
          localVideoRef.current.volume = 0;
        }

        // Update all peer connections with screen share
        Object.values(connections).forEach(call => {
          if (call.peerConnection) {
            const sender = call.peerConnection
              .getSenders()
              .find(s => s.track && s.track.kind === 'video');
            if (sender) {
              sender.replaceTrack(screenStream.getVideoTracks()[0]);
            }
          }
        });

        setIsScreenSharing(true);

        // Handle screen share being stopped by user (clicking "Stop Sharing" in browser)
        screenStream.getVideoTracks()[0].onended = async () => {
          // Only stop if still screen sharing (prevent double-stop)
          if (isScreenSharing) {
            if (screenStreamRef.current) {
              screenStreamRef.current.getTracks().forEach(track => track.stop());
            }
            await startLocalStream();
            setIsScreenSharing(false);
          }
        };
      } catch (err) {
        console.error('Error sharing screen:', err);
      }
    }
  };

  const copyMeetingLink = () => {
    const link = window.location.href;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), COPY_FEEDBACK_DURATION);
  };

  const handleAdComplete = () => {
    setAdCompleted(true);
    setShowAd(false);
    // Mark this meeting as watched in this session (persists on refresh, clears on tab close)
    sessionStorage.setItem(`quickmeet_ads_watched_${meetingId}`, 'true');
  };

  const togglePiP = async () => {
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPiPMode(false);
      } else if (localVideoRef.current && document.pictureInPictureEnabled) {
        // Check if video metadata is loaded
        if (localVideoRef.current.readyState < 1) {
          console.log('Video metadata not loaded yet, waiting...');
          // Wait for loadedmetadata event
          await new Promise((resolve) => {
            localVideoRef.current.addEventListener('loadedmetadata', resolve, { once: true });
          });
        }
        await localVideoRef.current.requestPictureInPicture();
        setIsPiPMode(true);
      }
    } catch (err) {
      console.error('Error toggling PiP:', err);
    }
  };

  // Raise hand functionality
  const toggleRaiseHand = async () => {
    if (isHandRaised) {
      // Lower hand
      setIsHandRaised(false);
      try {
        const handRef = doc(db, 'meetings', meetingId, 'raisedHands', user.uid);
        await deleteDoc(handRef);
      } catch (err) {
        console.error('Error lowering hand:', err);
      }
    } else {
      // Raise hand
      setIsHandRaised(true);
      try {
        const handRef = doc(db, 'meetings', meetingId, 'raisedHands', user.uid);
        await setDoc(handRef, {
          userId: user.uid,
          userName: user.email || 'Guest',
          timestamp: Date.now()
        });
      } catch (err) {
        console.error('Error raising hand:', err);
      }
    }
  };

  // Listen to raised hands
  useEffect(() => {
    if (!meetingId) return;

    const handsRef = collection(db, 'meetings', meetingId, 'raisedHands');
    const unsubscribe = onSnapshot(handsRef, (snapshot) => {
      const hands = [];
      snapshot.forEach((doc) => {
        hands.push(doc.data());
      });

      // Play sound when new hand is raised
      setRaisedHands(prevHands => {
        if (hands.length > prevHands.length) {
          playSound(SOUNDS.RAISED_HAND);
        }
        return hands;
      });
    });

    return () => unsubscribe();
  }, [meetingId]);

  // Listen to meeting notes
  useEffect(() => {
    if (!meetingId) return;

    const notesRef = collection(db, 'meetings', meetingId, 'notes');
    const q = query(notesRef);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const meetingNotes = [];
      snapshot.forEach((doc) => {
        meetingNotes.push({
          id: doc.id,
          ...doc.data()
        });
      });
      // Sort by timestamp
      meetingNotes.sort((a, b) => a.timestamp - b.timestamp);
      setNotes(meetingNotes);
    });

    return () => unsubscribe();
  }, [meetingId]);

  // Fetch meeting data (including template) and handle iOS-compatible status
  useEffect(() => {
    const fetchMeetingData = async () => {
      if (!meetingId || !user) return;

      try {
        const meetingRef = doc(db, 'meetings', meetingId);
        const meetingSnap = await getDoc(meetingRef);

        if (!meetingSnap.exists()) {
          setMeetingStatus('not_found');
          return;
        }

        const data = meetingSnap.data();
        setMeetingData(data);

        // Handle iOS-compatible status field
        const status = data.status;
        const isHost = data.createdBy === user.uid;

        // Handle ended or cancelled meetings
        if (status === 'ended' || status === 'cancelled') {
          setMeetingStatus(status);
          return;
        }

        // Handle scheduled meeting
        if (status === 'scheduled') {
          const now = Math.floor(Date.now() / 1000);
          const scheduledTime = data.scheduledFor; // Seconds since epoch
          const hasActiveParticipants = (data.participants && data.participants.length > 0);

          if (isHost) {
            // Host can always start the meeting
            await updateDoc(meetingRef, {
              status: 'active',
              startedAt: serverTimestamp()
            });
            setMeetingStatus('active');
            setMeetingData(prev => ({ ...prev, status: 'active' }));
          } else if (hasActiveParticipants) {
            // Someone already in the meeting - allow join
            await updateDoc(meetingRef, { status: 'active' });
            setMeetingStatus('active');
          } else if (scheduledTime && now >= scheduledTime) {
            // Scheduled time has passed - allow anyone to join and start
            await updateDoc(meetingRef, {
              status: 'active',
              startedAt: serverTimestamp()
            });
            setMeetingStatus('active');
            setMeetingData(prev => ({ ...prev, status: 'active' }));
          } else {
            // Meeting scheduled for future - show waiting
            setMeetingStatus('scheduled');
            return;
          }
        } else if (status === 'waiting') {
          // 'waiting' means meeting is ready - allow anyone to join
          await updateDoc(meetingRef, {
            status: 'active',
            startedAt: data.startedAt || serverTimestamp()
          });
          setMeetingStatus('active');
          setMeetingData(prev => ({ ...prev, status: 'active' }));
        } else {
          // status === 'active' or no status - allow join
          setMeetingStatus('active');
        }

        // Legacy support: check isActive for older meetings without status field
        if (!status && data.isActive === false) {
          setMeetingStatus('ended');
          return;
        }
      } catch (err) {
        console.error('Error fetching meeting data:', err);
        setMeetingStatus('not_found');
      }
    };

    fetchMeetingData();
  }, [meetingId, user]);

  // Handle waiting room entry logic
  useEffect(() => {
    if (!meetingData || !user) return;

    const isHost = meetingData.createdBy === user.uid;

    // Skip waiting room logic if:
    // - Waiting room not enabled
    // - User is the host
    // - User already admitted or rejected
    if (!meetingData.waitingRoomEnabled || isHost || admissionStatus !== null) {
      return;
    }

    const handleWaitingRoom = async () => {
      try {
        // Add user to waiting room
        const waitingRef = doc(db, 'meetings', meetingId, 'waitingRoom', user.uid);
        await setDoc(waitingRef, {
          userId: user.uid,
          userName: user.email || user.displayName || 'Guest',
          userEmail: user.email || '',
          joinedAt: serverTimestamp(),
          status: 'waiting'
        });

        setInWaitingRoom(true);

        // Listen for admission/rejection
        let lastStatus = 'waiting';
        const unsubscribe = onSnapshot(waitingRef, (snapshot) => {
          if (!snapshot.exists()) {
            // Document was deleted
            // Only treat as rejection if last status wasn't admitted
            if (lastStatus !== 'admitted') {
              setAdmissionStatus('rejected');
              setInWaitingRoom(false);
            }
            return;
          }

          const data = snapshot.data();
          lastStatus = data.status;

          if (data.status === 'admitted') {
            setAdmissionStatus('admitted');
            setInWaitingRoom(false);
            // Clean up waiting room entry
            deleteDoc(waitingRef).catch(err => console.error('Error cleaning up waiting room:', err));
          } else if (data.status === 'rejected') {
            setAdmissionStatus('rejected');
            setInWaitingRoom(false);
          }
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Error handling waiting room:', error);
        // On error, allow them to join (fail open for better UX)
        setAdmissionStatus('admitted');
      }
    };

    handleWaitingRoom();
  }, [meetingData, user, meetingId, admissionStatus]);

  // Handle virtual background processing
  useEffect(() => {
    const handleBackgroundChange = async () => {
      // If background is none, stop processing
      if (virtualBackground.type === 'none') {
        if (backgroundProcessorRef.current) {
          await backgroundProcessorRef.current.stop();
          backgroundProcessorRef.current = null;
        }
        // Show original video
        if (localVideoRef.current && localStreamRef.current) {
          localVideoRef.current.srcObject = localStreamRef.current;
          // Ensure local video is muted to prevent hearing yourself
          localVideoRef.current.muted = true;
          localVideoRef.current.volume = 0;
        }
        return;
      }

      // Start background processing
      if (localVideoRef.current && localStreamRef.current) {
        try {
          // Create processor if it doesn't exist
          if (!backgroundProcessorRef.current) {
            backgroundProcessorRef.current = new VirtualBackgroundProcessor();
            await backgroundProcessorRef.current.initialize();
          }

          // Create a hidden video element for processing
          if (!processedCanvasRef.current) {
            processedCanvasRef.current = document.createElement('video');
            processedCanvasRef.current.srcObject = localStreamRef.current;
            // Mute to prevent audio playback (even though hidden)
            processedCanvasRef.current.muted = true;
            processedCanvasRef.current.volume = 0;
            processedCanvasRef.current.play();
          }

          // Start processing with the selected background
          await backgroundProcessorRef.current.start(
            processedCanvasRef.current,
            virtualBackground.type,
            virtualBackground.url || virtualBackground.gradient
          );

          // Get the processed canvas and update video
          const canvasStream = backgroundProcessorRef.current.getCanvasStream();
          if (canvasStream && localVideoRef.current) {
            localVideoRef.current.srcObject = canvasStream;
            // Mute local video to prevent hearing yourself
            localVideoRef.current.muted = true;
            localVideoRef.current.volume = 0;
          }

          console.log('✅ Virtual background active:', virtualBackground.type);
        } catch (err) {
          console.error('Error starting background processing:', err);
        }
      }
    };

    handleBackgroundChange();

    // Cleanup on unmount
    return () => {
      if (backgroundProcessorRef.current) {
        backgroundProcessorRef.current.stop();
      }
    };
  }, [virtualBackground]);

  // Track talk time (simplified - tracks when not muted)
  useEffect(() => {
    if (!user?.uid || isMuted) {
      if (talkTimeIntervalRef.current) {
        clearInterval(talkTimeIntervalRef.current);
        talkTimeIntervalRef.current = null;
      }
      return;
    }

    talkTimeIntervalRef.current = setInterval(() => {
      setTalkTime(prev => ({
        ...prev,
        [user.uid]: (prev[user.uid] || 0) + 1
      }));
    }, 1000);

    return () => {
      if (talkTimeIntervalRef.current) {
        clearInterval(talkTimeIntervalRef.current);
      }
    };
  }, [isMuted, user?.uid]);

  const leaveMeeting = async () => {
    try {
      // Cleanup streams and peer
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }
      // Cleanup signaling service
      if (signalingServiceRef.current) {
        signalingServiceRef.current.cleanup();
        signalingServiceRef.current = null;
      }

      // Update participants array to mark as inactive (for iOS compatibility)
      if (myPeerId && user?.uid) {
        try {
          const meetingDocRef = doc(db, 'meetings', meetingId);
          const meetingSnap = await getDoc(meetingDocRef);
          if (meetingSnap.exists()) {
            const data = meetingSnap.data();
            const leftAtSeconds = Math.floor(Date.now() / 1000);  // iOS uses seconds

            // Update both participants and participantsData arrays
            const participants = data.participants || [];
            const participantsData = data.participantsData || [];

            const updatedParticipants = participants.map(p =>
              p.id === myPeerId ? { ...p, isActive: false, leftAt: leftAtSeconds } : p
            );
            const updatedParticipantsData = participantsData.map(p =>
              (p.id === myPeerId || p.odcId === user.uid) ? { ...p, isActive: false, leftAt: leftAtSeconds } : p
            );

            // Check if host is leaving - if so, end the meeting
            const isHost = data.createdBy === user.uid;
            const activeParticipantsRemaining = updatedParticipants.filter(p => p.isActive).length;

            if (isHost || activeParticipantsRemaining === 0) {
              // Host leaving or no active participants - end the meeting
              await updateDoc(meetingDocRef, {
                participants: updatedParticipants,
                participantsData: updatedParticipantsData,
                status: 'ended',
                endedAt: serverTimestamp()
              });
            } else {
              // Just update participants
              await updateDoc(meetingDocRef, {
                participants: updatedParticipants,
                participantsData: updatedParticipantsData
              });
            }

            // Also delete from participants/ subcollection for iOS
            try {
              const participantDocRef = doc(db, 'meetings', meetingId, 'participants', myPeerId);
              await deleteDoc(participantDocRef);
            } catch (subErr) {
              console.error('Error removing from participants subcollection:', subErr);
            }
          }
        } catch (err) {
          console.error('Error updating participants array:', err);
        }
      }

      // Save to meeting history
      if (user && meetingStartTime) {
        const duration = Math.floor((Date.now() - meetingStartTime) / 1000);
        const historyRef = collection(db, 'users', user.uid, 'meetingHistory');
        await addDoc(historyRef, {
          meetingId,
          participants: participantsList,
          duration,
          startTime: new Date(meetingStartTime),
          endTime: new Date(),
          recordingId: null, // Link if recording exists
          chatSaved: meetingData?.saveChatHistory || false,
          meetingType: 'video',
          createdAt: serverTimestamp()
        });
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('Error leaving meeting:', error);
      navigate('/dashboard');
    }
  };

  // Helper function to calculate grid layout
  const calculateGridLayout = (totalParticipants) => {
    if (totalParticipants === 1) {
      return { cols: 1, rows: 1 };
    } else if (totalParticipants === 2) {
      return { cols: 2, rows: 1 };
    } else if (totalParticipants <= 4) {
      return { cols: 2, rows: 2 };
    } else if (totalParticipants <= 6) {
      return { cols: 3, rows: 2 };
    } else if (totalParticipants <= 9) {
      return { cols: 3, rows: 3 };
    } else if (totalParticipants <= 12) {
      return { cols: 4, rows: 3 };
    } else {
      return { cols: 5, rows: Math.ceil(totalParticipants / 5) };
    }
  };

  // Create recording canvas with audio mixing
  const createRecordingCanvas = () => {
    const canvas = document.createElement('canvas');
    // Using isPremium from useTier hook
    canvas.width = isPremium ? 1920 : 1280;
    canvas.height = isPremium ? 1080 : 720;

    const ctx = canvas.getContext('2d');
    recordingCanvasRef.current = canvas;

    const drawFrame = () => {
      // Keep drawing while canvas exists (don't check isRecording to avoid race condition)
      if (!recordingCanvasRef.current) return;

      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const totalParticipants = remotePeerIds.length + 1;
      const { cols, rows } = calculateGridLayout(totalParticipants);
      const videoWidth = canvas.width / cols;
      const videoHeight = canvas.height / rows;

      // Draw local video
      if (localVideoRef.current?.srcObject) {
        try {
          ctx.drawImage(localVideoRef.current, 0, 0, videoWidth, videoHeight);
        } catch (err) {
          console.error('Error drawing local video to canvas:', err);
        }
      }

      // Draw remote videos
      remotePeerIds.forEach((peerId, index) => {
        const video = remoteVideosRef.current[peerId];
        if (video?.srcObject) {
          try {
            const col = (index + 1) % cols;
            const row = Math.floor((index + 1) / cols);
            ctx.drawImage(video, col * videoWidth, row * videoHeight, videoWidth, videoHeight);
          } catch (err) {
            console.error('Error drawing remote video to canvas:', err);
          }
        }
      });

      recordingAnimationRef.current = requestAnimationFrame(drawFrame);
    };

    // Draw initial frame immediately so canvas has content when captureStream is called
    drawFrame();

    // Create audio context to mix audio from all participants
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioDestination = audioContext.createMediaStreamDestination();

      console.log('🎤 Audio Mixing Debug:', {
        hasLocalStream: !!localStreamRef.current,
        isMuted,
        remotePeerCount: Object.keys(remoteVideosRef.current).length
      });

      // Add local audio track if available
      if (localStreamRef.current) {
        const localAudioTracks = localStreamRef.current.getAudioTracks();
        console.log('🎤 Local audio tracks:', {
          count: localAudioTracks.length,
          tracks: localAudioTracks.map(t => ({ id: t.id, label: t.label, enabled: t.enabled, muted: t.muted })),
          isMuted
        });

        if (localAudioTracks.length > 0 && !isMuted) {
          const localAudioSource = audioContext.createMediaStreamSource(
            new MediaStream([localAudioTracks[0]])
          );
          localAudioSource.connect(audioDestination);
          console.log('✅ Added local audio to recording');
        } else {
          console.log('⚠️ Local audio NOT added:', localAudioTracks.length === 0 ? 'No audio tracks' : 'User is muted');
        }
      } else {
        console.log('⚠️ No local stream available');
      }

      // Add remote audio tracks from all connected peers
      let remoteAudioCount = 0;
      Object.keys(remoteVideosRef.current).forEach(peerId => {
        const videoElement = remoteVideosRef.current[peerId];
        console.log(`🎤 Remote peer ${peerId}:`, {
          hasElement: !!videoElement,
          hasSrcObject: !!videoElement?.srcObject
        });

        if (videoElement?.srcObject) {
          const audioTracks = videoElement.srcObject.getAudioTracks();
          console.log(`🎤 Remote audio tracks for ${peerId}:`, {
            count: audioTracks.length,
            tracks: audioTracks.map(t => ({ id: t.id, label: t.label, enabled: t.enabled, muted: t.muted }))
          });

          if (audioTracks.length > 0) {
            try {
              const remoteAudioSource = audioContext.createMediaStreamSource(
                new MediaStream([audioTracks[0]])
              );
              remoteAudioSource.connect(audioDestination);
              remoteAudioCount++;
              console.log(`✅ Added remote audio from peer ${peerId}`);
            } catch (err) {
              console.error(`❌ Could not add remote audio from peer ${peerId}:`, err);
            }
          } else {
            console.log(`⚠️ No audio tracks for peer ${peerId}`);
          }
        }
      });
      console.log(`✅ Total remote audio tracks added: ${remoteAudioCount}`);

      // Get video stream from canvas
      const fps = isPremium ? 30 : 30;
      const videoStream = canvas.captureStream(fps);

      console.log('🎥 Canvas video stream:', {
        videoTracks: videoStream.getVideoTracks().length,
        tracks: videoStream.getVideoTracks().map(t => ({ id: t.id, label: t.label, enabled: t.enabled }))
      });

      console.log('🔊 Audio destination stream:', {
        audioTracks: audioDestination.stream.getAudioTracks().length,
        tracks: audioDestination.stream.getAudioTracks().map(t => ({ id: t.id, label: t.label, enabled: t.enabled }))
      });

      // Combine canvas video with mixed audio
      const combinedStream = new MediaStream([
        ...videoStream.getVideoTracks(),
        ...audioDestination.stream.getAudioTracks()
      ]);

      console.log('🎬 FINAL Recording stream created:', {
        videoTracks: combinedStream.getVideoTracks().length,
        audioTracks: combinedStream.getAudioTracks().length,
        allTracks: combinedStream.getTracks().map(t => ({
          kind: t.kind,
          id: t.id,
          label: t.label,
          enabled: t.enabled,
          muted: t.muted,
          readyState: t.readyState
        }))
      });

      // Store audio context for cleanup
      canvas.audioContext = audioContext;

      return combinedStream;
    } catch (error) {
      console.error('Error creating audio mix for recording:', error);
      // Fallback to video-only if audio mixing fails
      const fps = isPremium ? 30 : 30;
      return canvas.captureStream(fps);
    }
  };

  // Handle recording toggle
  const handleRecordingToggle = async () => {
    // Only organizer can record
    if (!isOrganizer) {
      alert('Only the meeting organizer can record this meeting.');
      return;
    }

    if (isRecording) {
      // Stop recording
      if (recordingAnimationRef.current) {
        cancelAnimationFrame(recordingAnimationRef.current);
        recordingAnimationRef.current = null;
      }

      const blob = await stopRecording();

      if (blob) {
        // Get participant names
        const participants = [
          user?.email || 'You',
          ...remotePeerIds.map(peerId => participantData[peerId]?.userName || 'Guest')
        ];

        // Upload recording
        try {
          await uploadRecording(blob, meetingId, participants, meetingData?.name);
          console.log('Recording uploaded successfully!');
        } catch (error) {
          console.error('Failed to upload recording:', error);
          alert('Failed to upload recording. Please try again.');
        }
      }

      // Clear recording status in Firestore
      try {
        const meetingRef = doc(db, 'meetings', meetingId);
        await updateDoc(meetingRef, {
          isBeingRecorded: false,
          recordingStartedAt: null,
          recordingStartedBy: null
        });
      } catch (error) {
        console.error('Error clearing recording status:', error);
      }

      // Cleanup audio context if it exists
      if (recordingCanvasRef.current?.audioContext) {
        try {
          recordingCanvasRef.current.audioContext.close();
        } catch (err) {
          console.warn('Error closing audio context:', err);
        }
      }

      recordingCanvasRef.current = null;
    } else {
      // Start recording
      const stream = createRecordingCanvas();
      const result = await startRecording(stream);

      if (!result.success) {
        alert(result.message || 'Failed to start recording');
        if (recordingAnimationRef.current) {
          cancelAnimationFrame(recordingAnimationRef.current);
          recordingAnimationRef.current = null;
        }
        // Cleanup audio context if it exists
        if (recordingCanvasRef.current?.audioContext) {
          try {
            recordingCanvasRef.current.audioContext.close();
          } catch (err) {
            console.warn('Error closing audio context:', err);
          }
        }
        recordingCanvasRef.current = null;
        return;
      }

      // Play recording start sound
      playSound(SOUNDS.RECORDING_START);

      // Update meeting document to indicate recording in progress
      try {
        const meetingRef = doc(db, 'meetings', meetingId);
        await updateDoc(meetingRef, {
          isBeingRecorded: true,
          recordingStartedAt: serverTimestamp(),
          recordingStartedBy: user?.email || 'Organizer'
        });
      } catch (error) {
        console.error('Error updating recording status:', error);
      }
    }
  };

  // Format recording time as MM:SS
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Show ad player before joining meeting (unless premium or platform admin)
  if (showAd && !isPremium && !isAdmin && !adCompleted) {
    return <AdPlayer onAdComplete={handleAdComplete} userIsPremium={isPremium} />;
  }

  // Show waiting room UI if user is waiting
  if (inWaitingRoom) {
    return (
      <div className="min-h-screen bg-[#1c1c1e] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-[#2c2c2e] rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
            <svg className="w-10 h-10 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-white text-2xl font-bold mb-2">You're in the waiting room</h2>
          <p className="text-gray-400 mb-6">The host will let you in soon...</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-medium transition-all"
          >
            Leave Waiting Room
          </button>
        </div>
      </div>
    );
  }

  // Show rejection UI if user was rejected
  if (admissionStatus === 'rejected') {
    return (
      <div className="min-h-screen bg-[#1c1c1e] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-white text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-400 mb-6">The host has denied your request to join this meeting</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-medium transition-all"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show meeting not found UI
  if (meetingStatus === 'not_found') {
    return (
      <div className="min-h-screen bg-[#1c1c1e] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-[#2c2c2e] rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-[#8e8e93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-white text-2xl font-bold mb-2">Meeting Not Found</h2>
          <p className="text-gray-400 mb-6">This meeting doesn't exist or the link is incorrect</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-[#06c] hover:bg-[#0055d4] text-white px-6 py-3 rounded-xl font-medium transition-all"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show meeting ended UI
  if (meetingStatus === 'ended') {
    return (
      <div className="min-h-screen bg-[#1c1c1e] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-[#2c2c2e] rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-[#8e8e93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-white text-2xl font-bold mb-2">Meeting Has Ended</h2>
          <p className="text-gray-400 mb-6">This meeting has already concluded</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-[#06c] hover:bg-[#0055d4] text-white px-6 py-3 rounded-xl font-medium transition-all"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show meeting cancelled UI
  if (meetingStatus === 'cancelled') {
    return (
      <div className="min-h-screen bg-[#1c1c1e] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-white text-2xl font-bold mb-2">Meeting Cancelled</h2>
          <p className="text-gray-400 mb-6">This meeting has been cancelled by the host</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-[#06c] hover:bg-[#0055d4] text-white px-6 py-3 rounded-xl font-medium transition-all"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show scheduled meeting UI (for non-hosts when meeting hasn't started)
  if (meetingStatus === 'scheduled') {
    const scheduledTime = meetingData?.scheduledFor;
    const scheduledDate = scheduledTime ? new Date(scheduledTime * 1000) : null;
    const isNowOrPast = scheduledTime && (Date.now() / 1000) >= scheduledTime;

    return (
      <div className="min-h-screen bg-[#1c1c1e] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-[#06c]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-[#06c]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-white text-2xl font-bold mb-2">Meeting Not Started</h2>
          <p className="text-gray-400 mb-2">{meetingData?.name || 'This meeting'} hasn't started yet</p>
          {scheduledDate && (
            <p className="text-[#06c] mb-4">
              Scheduled for {scheduledDate.toLocaleString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}
            </p>
          )}
          {isNowOrPast && (
            <p className="text-yellow-400 text-sm mb-4">
              The scheduled time has passed. The host may start it soon.
            </p>
          )}
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="bg-[#2c2c2e] hover:bg-[#3c3c3e] text-white px-6 py-3 rounded-xl font-medium transition-all"
            >
              Refresh
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-[#06c] hover:bg-[#0055d4] text-white px-6 py-3 rounded-xl font-medium transition-all"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black">
      {/* Top Overlay - Only visible when controls are visible */}
      {controlsVisible && (
        <div className="absolute top-0 left-0 right-0 z-30 bg-gradient-to-b from-black/60 to-transparent px-6 py-4 flex justify-between items-center transition-all duration-300 animate-fade-in">
          <div className="flex items-center gap-3">
            <span className="text-white/60 text-sm font-medium">
              {meetingData?.name || 'Meeting'}
            </span>
            {meetingStartTime && <MeetingTimer startTime={meetingStartTime} />}
          </div>
          <button
            onClick={leaveMeeting}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-xl flex items-center justify-center transition-all"
            title="Leave meeting"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Video Grid - Full screen, no padding */}
      <div className="absolute inset-0">
        {(() => {
          const totalParticipants = remotePeerIds.length + 1; // +1 for local user
          const hasFeatured = featuredParticipant !== null;

          // If someone is spotlighted (center stage), use speaker view layout
          if (hasFeatured && totalParticipants > 1) {
            return (
              <div className="w-full h-full flex flex-col">
                {/* Main spotlighted video - takes most of the screen */}
                <div className="flex-1 relative">
                  {featuredParticipant === 'local' ? (
                    // Local user is spotlighted
                    <div className="w-full h-full relative bg-black">
                      <video
                        ref={setLocalVideoRef}
                        autoPlay
                        muted
                        playsInline
                        className="w-full h-full object-contain bg-[#1c1c1e]"
                      />
                      {isVideoOff && (
                        <div className="absolute inset-0 bg-[#1c1c1e] flex items-center justify-center">
                          <div className="w-32 h-32 bg-[#2c2c2e] rounded-full flex items-center justify-center border-4 border-white/10">
                            <span className="text-white text-5xl font-semibold">
                              {user?.email?.[0]?.toUpperCase() || 'U'}
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="absolute bottom-4 left-4 px-4 py-2 bg-black/50 backdrop-blur-xl rounded-full">
                        <span className="text-white font-medium">You {isScreenSharing && '(Presenting)'}</span>
                      </div>
                      <button
                        onClick={() => setFeaturedParticipant(null)}
                        className="absolute top-4 right-4 px-3 py-1.5 bg-black/50 backdrop-blur-xl rounded-full text-white text-sm hover:bg-black/70 transition-all"
                      >
                        Exit Spotlight
                      </button>
                    </div>
                  ) : (
                    // Remote participant is spotlighted
                    <div className="w-full h-full relative bg-black">
                      <video
                        ref={(el) => {
                          if (el && featuredParticipant) {
                            remoteVideosRef.current[featuredParticipant] = el;
                          }
                        }}
                        autoPlay
                        playsInline
                        className="w-full h-full object-contain bg-[#1c1c1e]"
                      />
                      <div className="absolute bottom-4 left-4 px-4 py-2 bg-black/50 backdrop-blur-xl rounded-full">
                        <span className="text-white font-medium">
                          {participantData[featuredParticipant]?.userName || 'Participant'}
                        </span>
                      </div>
                      <button
                        onClick={() => setFeaturedParticipant(null)}
                        className="absolute top-4 right-4 px-3 py-1.5 bg-black/50 backdrop-blur-xl rounded-full text-white text-sm hover:bg-black/70 transition-all"
                      >
                        Exit Spotlight
                      </button>
                    </div>
                  )}
                </div>

                {/* Thumbnail strip at bottom */}
                <div className="h-28 bg-[#0a0a0a] flex items-center gap-2 px-4 overflow-x-auto">
                  {/* Local video thumbnail (if not spotlighted) */}
                  {featuredParticipant !== 'local' && (
                    <div
                      className="w-40 h-24 flex-shrink-0 relative bg-black rounded-xl overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#06c] transition-all"
                      onDoubleClick={() => setFeaturedParticipant('local')}
                    >
                      <video
                        ref={setLocalVideoRef}
                        autoPlay
                        muted
                        playsInline
                        className="w-full h-full object-cover"
                      />
                      {isVideoOff && (
                        <div className="absolute inset-0 bg-[#1c1c1e] flex items-center justify-center">
                          <div className="w-12 h-12 bg-[#2c2c2e] rounded-full flex items-center justify-center">
                            <span className="text-white text-lg font-semibold">
                              {user?.email?.[0]?.toUpperCase() || 'U'}
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="absolute bottom-1 left-1 px-2 py-0.5 bg-black/60 rounded text-white text-xs">You</div>
                    </div>
                  )}

                  {/* Remote video thumbnails (except spotlighted) */}
                  {remotePeerIds.filter(id => id !== featuredParticipant).map((peerId) => (
                    <div
                      key={peerId}
                      className="w-40 h-24 flex-shrink-0 relative bg-black rounded-xl overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#06c] transition-all"
                      onDoubleClick={() => setFeaturedParticipant(peerId)}
                    >
                      <video
                        ref={(el) => {
                          if (el) remoteVideosRef.current[peerId] = el;
                        }}
                        autoPlay
                        playsInline
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-1 left-1 px-2 py-0.5 bg-black/60 rounded text-white text-xs">
                        {participantData[peerId]?.userName || 'Participant'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          // Gallery view - calculate optimal grid layout
          let gridClasses = '';
          if (totalParticipants === 1) {
            gridClasses = 'grid-cols-1'; // Full screen
          } else if (totalParticipants === 2) {
            gridClasses = 'grid-cols-2'; // Side by side
          } else if (totalParticipants <= 4) {
            gridClasses = 'grid-cols-2'; // 2x2 grid
          } else if (totalParticipants <= 6) {
            gridClasses = 'grid-cols-3'; // 3x2 grid
          } else if (totalParticipants <= 9) {
            gridClasses = 'grid-cols-3'; // 3x3 grid
          } else if (totalParticipants <= 12) {
            gridClasses = 'grid-cols-4'; // 4x3 grid
          } else {
            gridClasses = 'grid-cols-4 lg:grid-cols-5'; // 5 columns for many participants
          }

          // Calculate rows needed for proper aspect ratio
          const rows = totalParticipants <= 2 ? 1 : totalParticipants <= 4 ? 2 : totalParticipants <= 6 ? 2 : totalParticipants <= 9 ? 3 : 3;
          const rowHeight = `repeat(${rows}, 1fr)`;

          return (
            <div
              className={`grid ${gridClasses} gap-1 w-full h-full p-1`}
              style={{ gridTemplateRows: rowHeight }}
            >
          {/* Local Video */}
          <div
            className="relative bg-black rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-white/30 transition-all"
            onDoubleClick={() => setFeaturedParticipant('local')}
          >
            <video
              ref={setLocalVideoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-contain bg-[#1c1c1e]"
              style={{
                display: 'block',
                position: 'relative',
                zIndex: 1,
                backgroundColor: '#000'
              }}
              onLoadedMetadata={(e) => {
                console.log('Video metadata loaded, readyState:', e.target.readyState);
                console.log('Video dimensions:', e.target.videoWidth, 'x', e.target.videoHeight);
              }}
              onPlay={() => console.log('Video started playing')}
              onError={(e) => console.error('Video error:', e)}
            />
            {isVideoOff && (
              <div className="absolute inset-0 bg-[#1c1c1e] flex items-center justify-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-[#2c2c2e] rounded-full flex items-center justify-center border-4 border-white/10">
                  <span className="text-white text-2xl sm:text-3xl font-semibold">
                    {user?.email?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
              </div>
            )}
            <div className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 px-2 sm:px-3 py-1 sm:py-1.5 bg-black/50 backdrop-blur-xl rounded-full">
              <span className="text-white text-xs sm:text-sm font-medium">
                You {isScreenSharing && '(Presenting)'}
              </span>
            </div>
            {isMuted && (
              <div className="absolute top-2 right-2 sm:top-3 sm:right-3 w-5 h-5 sm:w-6 sm:h-6 bg-red-500/90 rounded-full flex items-center justify-center">
                <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
              </div>
            )}
            {/* Double-click hint on hover */}
            <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-all flex items-center justify-center opacity-0 hover:opacity-100">
              <span className="text-white/70 text-xs bg-black/50 px-2 py-1 rounded">Double-click to spotlight</span>
            </div>
          </div>

          {/* Remote Videos */}
          {remotePeerIds.map((peerId) => (
            <div
              key={peerId}
              className="relative bg-black rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-white/30 transition-all"
              onDoubleClick={() => setFeaturedParticipant(peerId)}
            >
              <video
                ref={(el) => {
                  if (el && !remoteVideosRef.current[peerId]) {
                    remoteVideosRef.current[peerId] = el;
                    console.log('🎥 Remote video element mounted for peer:', peerId);
                  }
                }}
                autoPlay
                playsInline
                className="w-full h-full object-cover bg-[#1c1c1e]"
                onLoadedMetadata={(e) => {
                  console.log(`Remote video metadata loaded for ${peerId}:`, e.target.videoWidth, 'x', e.target.videoHeight);
                }}
                onPlay={() => console.log(`Remote video playing for ${peerId}`)}
                onError={(e) => console.error(`Remote video error for ${peerId}:`, e)}
              />
              <div className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 px-2 sm:px-3 py-1 sm:py-1.5 bg-black/50 backdrop-blur-xl rounded-full">
                <span className="text-white text-xs sm:text-sm font-medium">
                  {participantData[peerId]?.userName || 'Participant'}
                </span>
              </div>
              {/* Double-click hint on hover */}
              <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-all flex items-center justify-center opacity-0 hover:opacity-100">
                <span className="text-white/70 text-xs bg-black/50 px-2 py-1 rounded">Double-click to spotlight</span>
              </div>
            </div>
          ))}
        </div>
          );
        })()}

        {/* Waiting State */}
        {remotePeerIds.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none animate-fade-in">
            <div className="bg-[#1c1c1e] rounded-3xl p-12 max-w-md text-center border border-white/10 pointer-events-auto shadow-2xl">
              <div className="w-20 h-20 bg-[#2c2c2e] rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-white/5 animate-pulse">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-white text-2xl font-semibold mb-3">Waiting for others to join</h3>
              <p className="text-[#8e8e93] mb-6 leading-relaxed">
                Share the meeting link to invite participants
              </p>

              {/* P2P Privacy Notice */}
              <div className="mb-6 px-4 py-3 bg-green-500/10 border border-green-500/20 rounded-2xl">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-green-400 text-sm font-semibold">100% Private & Secure</span>
                </div>
                <p className="text-[#8e8e93] text-xs leading-relaxed">
                  Direct peer-to-peer connection. Your video never touches our servers.
                </p>
              </div>

              <button
                onClick={() => setShowShareModal(true)}
                className="px-6 py-3 bg-white text-black rounded-full font-medium hover:bg-gray-100 transition-all"
              >
                Share Meeting
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Floating Control Bar - Only visible when controls are visible */}
      {controlsVisible && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-40 animate-fade-in">
          <div className="bg-black/50 backdrop-blur-xl rounded-2xl px-4 py-3 shadow-2xl flex items-center gap-1">

            {/* LEFT GROUP - Core Media Controls */}
            <div className="flex items-center gap-1 px-2">
              {/* Mute Button */}
              <button
                onClick={toggleMute}
                className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                  isMuted ? 'bg-red-500 hover:bg-red-600' : 'bg-white/10 hover:bg-white/20'
                }`}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMuted ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  )}
                </svg>
              </button>

              {/* Video Button */}
              <button
                onClick={toggleVideo}
                className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                  isVideoOff ? 'bg-red-500 hover:bg-red-600' : 'bg-white/10 hover:bg-white/20'
                }`}
                title={isVideoOff ? 'Turn on camera' : 'Turn off camera'}
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isVideoOff ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  )}
                </svg>
              </button>

              {/* Screen Share Button */}
              <button
                onClick={toggleScreenShare}
                className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                  isScreenSharing ? 'bg-green-500 hover:bg-green-600' : 'bg-white/10 hover:bg-white/20'
                }`}
                title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </button>
            </div>

            {/* Divider */}
            <div className="w-px h-8 bg-white/20 mx-1" />

            {/* CENTER - End Call */}
            <button
              onClick={leaveMeeting}
              className="w-14 h-12 rounded-xl bg-red-500 hover:bg-red-600 flex items-center justify-center transition-all mx-1"
              title="Leave meeting"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
              </svg>
            </button>

            {/* Divider */}
            <div className="w-px h-8 bg-white/20 mx-1" />

            {/* RIGHT GROUP - Panels */}
            <div className="flex items-center gap-1 px-2">
              {/* Participants Button */}
              <button
                onClick={() => setShowParticipants(!showParticipants)}
                className={`relative w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                  showParticipants ? 'bg-[#06c] hover:bg-[#0055d4]' : 'bg-white/10 hover:bg-white/20'
                }`}
                title="Participants"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-white rounded-full flex items-center justify-center">
                  <span className="text-black text-[10px] font-bold">{remotePeerIds.length + 1}</span>
                </div>
              </button>

              {/* Chat Button */}
              <button
                onClick={() => setIsChatOpen(!isChatOpen)}
                className={`relative w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                  isChatOpen ? 'bg-[#06c] hover:bg-[#0055d4]' : 'bg-white/10 hover:bg-white/20'
                }`}
                title="Chat"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {unreadCount > 0 && !isChatOpen && remotePeerIds.length > 0 && (
                  <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-[10px] font-bold">{unreadCount}</span>
                  </div>
                )}
              </button>

              {/* Notes Button */}
              <button
                onClick={() => setIsNotesOpen(!isNotesOpen)}
                className={`relative w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                  isNotesOpen ? 'bg-[#06c] hover:bg-[#0055d4]' : 'bg-white/10 hover:bg-white/20'
                }`}
                title={isPremium ? 'Notes' : 'Notes (Pro)'}
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {!isPremium && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-[#06c] to-[#0055d4] rounded-full flex items-center justify-center">
                    <span className="text-white text-[8px] font-bold">P</span>
                  </div>
                )}
              </button>

              {/* Share Button */}
              <button
                onClick={() => setShowShareModal(true)}
                className="w-12 h-12 rounded-xl flex items-center justify-center transition-all bg-white/10 hover:bg-white/20"
                title="Share"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </button>
            </div>

            {/* Divider */}
            <div className="w-px h-8 bg-white/20 mx-1" />

            {/* FAR RIGHT - Additional Controls */}
            <div className="flex items-center gap-1 px-2">
              {/* Raise Hand Button */}
              <button
                onClick={toggleRaiseHand}
                className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                  isHandRaised ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-white/10 hover:bg-white/20'
                }`}
                title={isHandRaised ? 'Lower hand' : 'Raise hand'}
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                </svg>
              </button>

              {/* Virtual Backgrounds Button */}
              <VirtualBackgrounds
                onSelectBackground={setVirtualBackground}
                currentBackground={virtualBackground.id}
                isOpen={activePopup === 'virtualBackground'}
                onToggle={togglePopup('virtualBackground')}
              />

              {/* Recording Button (Organizer/Admin Only) */}
              {(isOrganizer || isAdmin) && (
                <button
                  onClick={handleRecordingToggle}
                  disabled={isUploading}
                  className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                    isRecording
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-white/10 hover:bg-white/20'
                  } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={isRecording ? 'Stop recording' : 'Record'}
                >
                  {isUploading ? (
                    <svg className="w-5 h-5 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-white" fill={isRecording ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="5" strokeWidth={2} />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Participants Drawer */}
      {showParticipants && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          onClick={() => setShowParticipants(false)}
        >
          <div
            className="absolute right-0 top-0 bottom-0 w-full sm:w-96 bg-[#1c1c1e] shadow-2xl transform transition-transform"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-white text-xl font-semibold">Participants ({remotePeerIds.length + 1})</h2>
              <button
                onClick={() => setShowParticipants(false)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto h-full pb-32 p-4">
              {/* Local User */}
              <div className="bg-white/5 rounded-xl p-4 mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold">{user?.email?.[0]?.toUpperCase() || 'U'}</span>
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-medium">{user?.email || 'You'} (You)</div>
                    <div className="text-white/60 text-sm">Host</div>
                  </div>
                  {isMuted && (
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
              {/* Remote Participants */}
              {remotePeerIds.map((peerId) => (
                <div key={peerId} className="bg-white/5 rounded-xl p-4 mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold">
                        {participantData[peerId]?.userName?.[0]?.toUpperCase() || 'G'}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium">
                        {participantData[peerId]?.userName || 'Participant'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Chat Drawer */}
      {isChatOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          onClick={() => setIsChatOpen(false)}
        >
          <div
            className="absolute right-0 top-0 bottom-0 w-full sm:w-96 bg-[#1c1c1e] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <ChatBox
              messages={messages}
              onSendMessage={handleSendMessage}
              isOpen={isChatOpen}
              onClose={() => setIsChatOpen(false)}
              userName={user?.email || 'Guest'}
            />
          </div>
        </div>
      )}

      {/* Meeting Notes Drawer */}
      {isNotesOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          onClick={() => setIsNotesOpen(false)}
        >
          <div
            className="absolute right-0 top-0 bottom-0 w-full sm:w-96 bg-[#1c1c1e] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <MeetingNotes
              notes={notes}
              onAddNote={handleAddNote}
              isOpen={isNotesOpen}
              onClose={() => setIsNotesOpen(false)}
              userName={user?.email || 'Guest'}
              isPremium={isPremium}
              meetingStartTime={meetingStartTime}
              // Audio clip props
              audioClipState={{
                isBuffering: isClipBuffering,
                isRecording: isClipRecording,
                hasPermission: hasClipPermission
              }}
              onStartClipRecording={handleStartClipRecording}
              onPlayClip={(audioClip) => {
                // Play clip audio
                if (audioClip?.url) {
                  const audio = new Audio(audioClip.url);
                  audio.play().catch(console.warn);
                }
              }}
              pendingClip={pendingClipForNote}
              onClearPendingClip={() => setPendingClipForNote(null)}
            />
          </div>
        </div>
      )}

      {/* Recording Indicator - Visible to all participants */}
      {(isRecording || meetingData?.isBeingRecorded) && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 animate-fade-in">
          <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
          <span className="font-medium text-sm">
            {isOrganizer && isRecording
              ? `Recording: ${formatRecordingTime(recordingTime)}`
              : `Recording in progress${meetingData?.recordingStartedBy ? ` by ${meetingData.recordingStartedBy}` : ''}`
            }
          </span>
        </div>
      )}

      {/* Upload Progress */}
      {isUploading && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-black/80 backdrop-blur-xl text-white px-6 py-3 rounded-2xl shadow-2xl min-w-[300px] animate-fade-in">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <span className="font-medium text-sm">Uploading recording... {uploadProgress}%</span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-blue-600 h-full transition-all duration-300 rounded-full"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Personal Context Clip Recording Indicator */}
      <ClipRecordingIndicator
        isRecording={isClipRecording}
        recordingTime={clipRecordingTime}
        maxDuration={clipMaxDuration}
        isMeetingMuted={isMeetingMicMuted}
        onStop={handleStopClipRecording}
      />

      {/* Waiting Room Queue - Hidden but functional */}
      <div className="hidden">
        <WaitingRoomQueue
          meetingId={meetingId}
          isHost={isOrganizer}
        />
      </div>

      {/* Raise Hand Queue - Hidden but functional */}
      <div className="hidden">
        <RaiseHandQueue
          raisedHands={raisedHands}
          onLowerHand={async (userId) => {
            try {
              const handRef = doc(db, 'meetings', meetingId, 'raisedHands', userId);
              await deleteDoc(handRef);
            } catch (err) {
              console.error('Error lowering hand:', err);
            }
          }}
          currentUserId={user?.uid}
          isOrganizer={isOrganizer}
          onSelectSpeaker={(speakerUserId) => {
            Object.values(dataConnectionsRef.current).forEach(conn => {
              if (conn && conn.open) {
                conn.send({
                  type: 'mute_others',
                  speakerUserId,
                  organizerId: user?.uid
                });
              }
            });
            if (user?.uid !== speakerUserId) {
              if (localStreamRef.current) {
                const audioTrack = localStreamRef.current.getAudioTracks()[0];
                if (audioTrack) {
                  audioTrack.enabled = false;
                  setIsMuted(true);
                }
              }
            }
          }}
        />
      </div>

      {/* Meeting Prompts - Hidden but functional */}
      {meetingData?.template && (
        <div className="hidden">
          <MeetingPrompts
            template={meetingData.template}
            startTime={meetingStartTime}
            isHost={isOrganizer}
          />
        </div>
      )}

      {/* Share Meeting Modal */}
      <ShareMeetingModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        meetingId={meetingId}
        meetingName={meetingData?.name || null}
        accessCode={meetingData?.accessCode || null}
      />
    </div>
  );
}
